import {
  Button,
  Flex,
  Modal,
  SearchField,
  Typography,
} from '@iheartjane/reefer';
import { sentenceCase } from '@iheartjane/utils';
import type { ReactNode } from 'react';

import type { ConditionSelectionType } from './conditions.types';
import { ConditionSelectionTypeRadio } from './conditionSelectionTypeRadio';

export interface ConditionModalProps {
  children?: ReactNode;

  /** The section's heading, ex "Categories" */
  label: string;

  onSave(): void;

  /** Omit to hide the search field */
  onSearchChange?(searchQuery: string): void;

  /** Omit to hide "Select all", ex when the options are fetched a page at a time */
  onSelectAll?(): void;

  /** Omit to hide the include/exclude radio */
  onSelectionTypeChange?(selectionType: ConditionSelectionType): void;

  onSelectNone(): void;

  saveDisabled?: boolean;

  searchQuery?: string;

  /** Number of selections in this section */
  selectedCount: number;

  selectionType?: ConditionSelectionType;
}

export const ConditionModal = ({
  children,
  label,
  onSave,
  onSearchChange,
  onSelectAll,
  onSelectionTypeChange,
  onSelectNone,
  saveDisabled,
  searchQuery,
  selectedCount,
  selectionType,
}: ConditionModalProps) => {
  const condition = label.toLowerCase();

  return (
    <>
      <Modal.Header
        title={sentenceCase(`Select ${label}`)}
        actions={
          <Button
            label="Save"
            onClick={onSave}
            variant="primary"
            disabled={saveDisabled}
          />
        }
      />
      <Modal.Content>
        {selectionType && onSelectionTypeChange && (
          <ConditionSelectionTypeRadio
            noun={condition}
            onChange={onSelectionTypeChange}
            value={selectionType}
          />
        )}
        {onSearchChange && (
          <Flex pb={16}>
            <SearchField
              label={`Search ${condition}`}
              name="condition-search"
              onChange={onSearchChange}
              placeholder={`Search ${condition}`}
              value={searchQuery}
              width="100%"
              disableMobileInputStyling
            />
          </Flex>
        )}
        {children}
      </Modal.Content>
      <Modal.Footer>
        <Flex alignItems="center" justifyContent="space-between" width="100%">
          <Typography color="grays-mid">{selectedCount} selected</Typography>
          <Flex alignItems="center" gap={16}>
            {onSelectAll && (
              <Button
                label="Select all"
                onClick={onSelectAll}
                variant="tertiary"
              />
            )}
            <Button
              label="Select none"
              onClick={onSelectNone}
              variant="tertiary"
              disabled={selectedCount === 0}
            />
          </Flex>
        </Flex>
      </Modal.Footer>
    </>
  );
};
